// ABCRPT1 (docs/build-briefs/ABCRPT1-abc-spirits-used-vs-sold.md): the pure rules behind the Spirits used vs sold (ABC)
// screen. No DOM. A missing figure is a dash on screen and its plain reason is the hover text; the screen never turns a
// missing count into a 0.

import type { AbcAgreementDto, AbcFigureDto, AbcSpiritsReportDto } from "./api.ts";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** "2026-09" as "September 2026". */
export function monthLabel(month: string): string {
  const [year, mon] = month.split("-");
  return `${MONTH_NAMES[Number(mon) - 1] ?? ""} ${year ?? ""}`.trim();
}

/** The month before or after `month` ("2026-01" back one is "2025-12"). */
export function shiftMonth(month: string, by: number): string {
  const [year, mon] = month.split("-").map(Number) as [number, number];
  const index = year * 12 + (mon - 1) + by;
  const nextYear = Math.floor(index / 12);
  return `${nextYear}-${String((index % 12) + 1).padStart(2, "0")}`;
}

export function canShowNextMonth(month: string, today: string): boolean {
  return month < today.slice(0, 7);
}

export interface FigureCell {
  text: string;
  /** The plain reason when there is no figure, for the hover. */
  reason: string | null;
}

/** Bottles to two decimals; a missing figure is a dash with its reason. */
export function bottleCell(figure: AbcFigureDto): FigureCell {
  if (figure.missing !== null) return { text: "-", reason: figure.missing };
  if (figure.bottles === null) {
    return {
      text: "-",
      reason: "This item has no bottle size recorded, so its ounces cannot be shown as bottles.",
    };
  }
  return { text: Number(figure.bottles).toFixed(2), reason: null };
}

const AGREEMENT_TEXT: Record<AbcAgreementDto, string> = {
  agree: "Agrees",
  differ: "Differs",
  unknown: "Needs counts",
};
export const agreementText = (agreement: AbcAgreementDto): string => AGREEMENT_TEXT[agreement];

export function sizeText(label: string | null): string {
  return label ?? "No size recorded";
}

/** One sentence naming how much of the month could not be compared, or null when everything was counted. */
export function missingSummary(report: AbcSpiritsReportDto): string | null {
  const total = report.rows.length;
  const uncounted = report.rows.filter((r) => r.usedCounted.missing !== null).length;
  if (total === 0 || uncounted === 0) return null;
  return `${uncounted} of ${total} ${total === 1 ? "item has" : "items have"} no count at one or both ends of the month, so counted use is left blank for ${uncounted === 1 ? "it" : "them"}. Count them to compare.`;
}

export function monthStatus(report: AbcSpiritsReportDto): string {
  return report.meta.partialMonth
    ? `${monthLabel(report.meta.month)} so far. The month is not over.`
    : monthLabel(report.meta.month);
}
